'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'

import { cn } from '../../lib/utils'

import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay as OriginDialogOverlay,
  DialogHeader as OriginDialogHeader,
  DialogFooter as OriginDialogFooter,
  DialogTitle as OriginDialogTitle,
  DialogDescription,
} from '../ui/dialog'

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <OriginDialogOverlay
    ref={ref}
    className={cn('z-[99] overflow-hidden bg-black/80', className)}
    {...props}
  />
))

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    onClickClose?: () => void
    showCloseButton?: boolean
    invertColorCloseButton?: boolean
  }
>(
  (
    {
      className,
      children,
      onClickClose,
      showCloseButton = true,
      invertColorCloseButton = false,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-[-50%] data-[state=closed]:slide-out-to-bottom-[-50%] data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2 fixed left-[50%] top-[50%] z-[99] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-2xl p-6 shadow-lg duration-200 md:max-h-[90vh]',
          'outline-none focus:outline-none focus-visible:outline-none',
          className,
        )}
        style={{
          // No marginTop / top-inset here: it is only non-zero in an
          // edge-to-edge webview (the Oddle Eats app), which already offsets
          // the webview below the status bar via its native header. A top
          // inset shifted this centred dialog down and left a gap above it.
          marginBottom: 'env(safe-area-inset-bottom, 0)',
          // Cap height to the viewport minus the bottom inset only.
          maxHeight: 'calc(100vh - env(safe-area-inset-bottom, 0))',
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <div
            className={cn(
              'data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-2.5 z-10 flex rounded-full p-1.5',
              invertColorCloseButton ? 'bg-[#00000080]' : 'bg-transparent',
            )}
          >
            <DialogPrimitive.Close
              data-testid="button.close"
              className="outline-none disabled:pointer-events-none"
              onClick={onClickClose ? () => onClickClose() : undefined}
            >
              <XIcon
                className={cn(
                  'h-6 w-6 ',
                  invertColorCloseButton ? 'text-white' : 'text-black',
                )}
              />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <OriginDialogHeader
    className={cn('space-y-0 rounded-t-none md:rounded-t-2xl', className)}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <OriginDialogFooter
    className={cn(
      'bg-container z-10 rounded-b-none md:rounded-b-2xl',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <OriginDialogTitle
    ref={ref}
    className={cn('tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
